// 直書網格分頁的純函式與常數（自 WordGridModalComponent.js 抽出）
// 無 React／無 store 依賴，僅用瀏覽器 DOM API（document / NodeFilter）。
// 保留 NBK-1512 的 glyphBreak 語意分頁邏輯。

// === 新增：直書網格與分頁工具 ===
// 每行固定 8 字（直向 8 格），每頁顯示 10 行，加上最上方 1 行生字框，共 11 行
export const ROWS_PER_COL = 8; // 內文每列（直排每欄）固定 8 格
// 注意：不同裝置/視窗寬度不一，「每頁欄數」必須依寬度動態決定。
// 這裡保留一個初始值/回退值（在尚未量到容器寬度之前使用）。
export const DEFAULT_COLS_PER_PAGE = 11;
export const EXTRA_WORD_ROW = 1; // 生字框 1 行（占用一欄位高度）
// 針對語詞/標籤（解、例）與一般內文在單欄可容納的格數
export const ROWS_NORMAL = ROWS_PER_COL;        // 一般 8 格
export const ROWS_WORD = Math.max(1, ROWS_PER_COL - 2); // 生字框大格，估計 6 格高度
export const ROWS_TAG = Math.max(1, ROWS_PER_COL - 1);  // "解"、"例" 標籤，估計 7 格高度
export const CELL_GAP = 0; // 無間距
export const CELL_HEIGHT_RATIO = 1; // 與 CSS --cellH 一致，依高度填滿

export function getRubyBaseText(node) {
  if (!node) return '';
  const clone = node.cloneNode(true);
  if (clone.querySelectorAll) {
    const rts = clone.querySelectorAll('rt');
    for (let i = 0; i < rts.length; i++) {
      const rt = rts[i];
      if (rt && rt.parentNode) rt.parentNode.removeChild(rt);
    }
  }
  return (clone.textContent || '').trim();
}

export function splitWordAndBody(html) {
  const tpl = document.createElement('template');
  tpl.innerHTML = html || '';

  // 注意：此工具函式原本假設「單一詞條內容」只會有一個 `.word`（生字大框）。
  // 但實務上有資料會在同一份 HTML 內出現多個 `.word`（多個詞條串在一起）。
  // 在這種情況下，`titleRubies` 只能取第一個 `.word`，避免把多個標題串成同一個生字框而導致後續標題消失。
  const firstWord = tpl.content.querySelector('.word');
  const titleRubies = firstWord
    ? Array.from(firstWord.querySelectorAll('ruby,sign')).map(x => x.outerHTML)
    : [];
  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      const tag = node.tagName ? node.tagName.toLowerCase() : '';
      if (tag === 'ruby' || tag === 'sign') {
        if (node.closest('.word') || node.closest('.wordSense') || node.closest('.private_name') || node.closest('.flow_wave')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
      if (tag === 'span' && node.classList && (node.classList.contains('private_name') || node.classList.contains('flow_wave'))) {
        if (node.closest('.word')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
      if (tag === 'img') return NodeFilter.FILTER_ACCEPT;
      if (tag === 'span' && node.classList && node.classList.contains('wordSense')) return NodeFilter.FILTER_ACCEPT;
      if (tag === 'div' && node.classList && node.classList.contains('word')) return NodeFilter.FILTER_ACCEPT;
      return NodeFilter.FILTER_SKIP;
    }
  });
  const body = [];
  let n = walker.nextNode();
  while (n) {
    const tag = n.tagName ? n.tagName.toLowerCase() : '';
    if (!(tag === 'div' && n.classList && n.classList.contains('word'))) {
      // 特例：若為圖片，保留可能存在的 YouTube 連結包裹
      if (tag === 'img') {
        let htmlFrag = n.outerHTML;
        const anchor = n.closest && n.closest('a[href]');
        const inYoutube = !!(n.closest && n.closest('.youtube'));
        let href = null;
        if (anchor && anchor.getAttribute) href = anchor.getAttribute('href');
        if (!href && inYoutube) {
          const wrap = n.closest && n.closest('.youtube');
          const a = wrap && wrap.querySelector ? wrap.querySelector('a[href]') : null;
          if (a) href = a.getAttribute('href');
        }
        if (!href) {
          const dataHref = n.getAttribute && n.getAttribute('data-href');
          if (dataHref) href = dataHref;
        }
        if (href) {
          const cls = (anchor && anchor.getAttribute && anchor.getAttribute('class')) || (inYoutube ? 'youtube' : '');
          const classAttr = cls ? ` class="${cls}"` : '';
          htmlFrag = `<a${classAttr} href="${href}" target="_blank" rel="noopener noreferrer">${htmlFrag}</a>`;
        }
        body.push({ html: htmlFrag, base: '' });
      } else {
        const isWordSense = tag === 'span' && n.classList && n.classList.contains('wordSense');
        body.push({ html: n.outerHTML, base: isWordSense ? getRubyBaseText(n) : '' });
      }
    }
    n = walker.nextNode();
  }
  return { titleRubies, bodyTokens: body };
}

export function extractSegments(html, opts = {}) {
  const { includeWord = true } = opts;
  // 若同一段 HTML 內含多個 `.word`（多詞條），不要用「單詞條」的抽取規則，
  // 否則會把多個標題合併成單一生字框，造成第二個詞條（如：葡萄柚）不見，並讓分頁看起來跳頁。
  // 多詞條情境：直接回傳原始 HTML（保留所有 `.word`），讓後續分頁以 `.word` 為新頁起點。
  if (includeWord) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html || '';
    const wordCount = tpl.content.querySelectorAll('.word').length;
    if (wordCount > 1) {
      // 仍偵測是否含「解」標籤，讓外層流程至少能產生一筆條目（用於連續內容渲染）
      const senses = Array.from(tpl.content.querySelectorAll('.wordSense'));
      const hasExplain = senses.some(s => getRubyBaseText(s) === '解');
      return {
        hasExplain: hasExplain || wordCount > 0,
        hasExample: false,
        explainHTML: `<div>${tpl.innerHTML}</div>`,
        exampleHTML: ''
      };
    }
  }
  const { titleRubies, bodyTokens } = splitWordAndBody(html || '');
  const idxExplain = bodyTokens.findIndex(t => t.base === '解');
  const idxExample = bodyTokens.findIndex(t => t.base === '例');
  // 新增：將位於「解」之前的內容（例如：成語本體，如「無理取鬧」）保留，
  // 並在輸出 explainHTML 時放在生字大框(.word)之後、解釋內容之前，
  // 以回復原本「生字左邊先排成語，接著才是『解』」的版面。
  const beforeExplain = idxExplain >= 0
    ? bodyTokens.slice(0, idxExplain).map(t => t.html)
    : [];
  const explainSlice = idxExplain >= 0
    ? bodyTokens.slice(idxExplain, (idxExample > idxExplain ? idxExample : bodyTokens.length)).map(t => t.html)
    : [];
  const exampleSlice = idxExample >= 0 ? bodyTokens.slice(idxExample).map(t => t.html) : [];
  return {
    hasExplain: idxExplain >= 0 && explainSlice.length > 0,
    hasExample: idxExample >= 0 && exampleSlice.length > 0,
    explainHTML: idxExplain >= 0 ? `<div>${includeWord ? `<div class="word">${titleRubies.join('')}</div>` : ''}${beforeExplain.join('')}${explainSlice.join('')}</div>` : '',
    exampleHTML: idxExample >= 0 ? `<div>${exampleSlice.join('')}</div>` : ''
  };
}

export function stripTitleWord(html) {
  const { bodyTokens } = splitWordAndBody(html || '');
  return bodyTokens.map(t => t.html).join('');
}

export function htmlToColumns(html, opts = {}) {
  const glyphMode = !!opts.glyphMode;
  const tpl = document.createElement('template');
  tpl.innerHTML = html || '';
  // 建立 <a id=... href=...> 對照表，供圖片無包 a 時補上
  const anchorIdToHref = new Map();
  Array.from(tpl.content.querySelectorAll('a[href]')).forEach(a => {
    const aid = a.getAttribute('id') || a.getAttribute('name') || a.getAttribute('data-id');
    const href = a.getAttribute('href');
    if (aid && href) anchorIdToHref.set(aid, href);
  });
  const { titleRubies } = splitWordAndBody(html || '');
  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      const tag = node.tagName ? node.tagName.toLowerCase() : '';
      if (tag === 'div' && node.classList && node.classList.contains('word')) return NodeFilter.FILTER_ACCEPT;
      if (tag === 'span' && node.classList && node.classList.contains('wordSense')) return NodeFilter.FILTER_ACCEPT;
      if (tag === 'ruby' || tag === 'sign') {
        if (node.closest('.word') || node.closest('.wordSense') || node.closest('.private_name') || node.closest('.flow_wave')) return NodeFilter.FILTER_REJECT;
        const wsc = node.closest('.wordSenseContent');
        if (wsc) {
          const prev = wsc.previousElementSibling;
          if (prev && prev.classList && prev.classList.contains('wordSense')) return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
      if (tag === 'span' && node.classList && (node.classList.contains('private_name') || node.classList.contains('flow_wave'))) {
        if (node.closest('.word')) return NodeFilter.FILTER_REJECT;
        const wsc = node.closest('.wordSenseContent');
        if (wsc) {
          const prev = wsc.previousElementSibling;
          if (prev && prev.classList && prev.classList.contains('wordSense')) return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
      // 字音字形：把 <img> 視為分段起點（欄首圖片）
      if (tag === 'img') return NodeFilter.FILTER_ACCEPT;
      if (tag === 'br') {
        const parent = node.parentElement;
        if (parent && parent.classList && parent.classList.contains('article')) {
          return NodeFilter.FILTER_ACCEPT;
        }
      }
      return NodeFilter.FILTER_SKIP;
    }
  });
  // 專名號（.private_name 直線）／書名號（.flow_wave 波浪線）：
  // 改為逐字（每個 ruby/sign）拆成獨立格子，讓文字與其他字一樣進入格狀排列、
  // 不再因整段 span 不拆而溢出欄位。原 span 的 class（含 red/pink/blue 變體）以 pnClass
  // 帶到 item，於渲染時掛在 .cell 上，使 border-left + border-image 畫在整格左緣、
  // 上下相鄰格的線自然相接（不再包一層 span、也不再畫在 ruby 上）。
  const splitPNItems = (node) => {
    const cls = (node.getAttribute && node.getAttribute('class')) || '';
    const inner = node.querySelectorAll ? node.querySelectorAll('ruby, sign') : [];
    if (!inner || !inner.length) return [{ html: node.innerHTML, pnClass: cls }];
    return Array.from(inner).map(r => ({ html: r.outerHTML, pnClass: cls }));
  };
  const tokens = [];
  // 字音字形（glyphSet）分頁用：追蹤 wordRadical 圖片在文件順序中的角色，
  // 以判定每個圖片是否為「新頁起點」。glyphImgIndex 為已處理的 wordRadical 圖片數，
  // prevGlyphRole 為前一個 wordRadical 圖片的角色（'reading' 讀音 / 'similar' 形近字）。
  let glyphImgIndex = 0;
  let prevGlyphRole = null;
  let n = walker.nextNode();
  while (n) {
    const tag = n.tagName ? n.tagName.toLowerCase() : '';
    if (tag === 'div' && n.classList && n.classList.contains('word')) {
      const items = Array.from(n.querySelectorAll('ruby,sign')).map(x => ({ html: x.outerHTML, kind: 'word' }));
      tokens.push({ kind: 'word', items });
    } else if (tag === 'span' && n.classList && n.classList.contains('wordSense')) {
      const base = getRubyBaseText(n);
      const sub = base === '解' ? 'jie' : (base === '例' ? 'li' : (base === '詞' ? 'ci' : null));
      const isBlue = n.classList && n.classList.contains('blue');
      const ruby = n.querySelector('ruby');
      const htmlFrag = ruby ? ruby.outerHTML : n.outerHTML;
      const items = [{ html: htmlFrag }];
      const nextSib = n.nextElementSibling;
      if (nextSib && nextSib.classList && nextSib.classList.contains('wordSenseContent')) {
        const processed = new Set();
        Array.from(nextSib.querySelectorAll('.private_name, .flow_wave, ruby, sign')).forEach(x => {
          if (processed.has(x)) return;
          if (x.classList && (x.classList.contains('private_name') || x.classList.contains('flow_wave'))) {
            splitPNItems(x).forEach(it => items.push(it));
            x.querySelectorAll('ruby, sign').forEach(r => processed.add(r));
            processed.add(x);
          } else {
            const pn = x.closest('.private_name') || x.closest('.flow_wave');
            if (pn && nextSib.contains(pn)) {
              if (!processed.has(pn)) {
                splitPNItems(pn).forEach(it => items.push(it));
                pn.querySelectorAll('ruby, sign').forEach(r => processed.add(r));
                processed.add(pn);
              }
            } else {
              items.push({ html: x.outerHTML });
            }
          }
        });
      }
      tokens.push({ kind: 'tag', sub, follow: isBlue ? 'blue' : null, items });
    } else if (tag === 'img') {
      // 若圖片在 <a href> 或 .youtube 容器內，或可由 id 對應到 <a id=... href=...>，則補上連結
      let htmlFrag = n.outerHTML;
      const anchor = n.closest && n.closest('a[href]');
      const inYoutube = !!(n.closest && n.closest('.youtube'));
      let href = null;
      if (anchor && anchor.getAttribute) href = anchor.getAttribute('href');
      if (!href) {
        const id = n.getAttribute && n.getAttribute('id');
        if (id && anchorIdToHref.has(id)) href = anchorIdToHref.get(id);
      }
      if (!href && inYoutube) {
        const wrap = n.closest && n.closest('.youtube');
        const a = wrap && wrap.querySelector ? wrap.querySelector('a[href]') : null;
        if (a) href = a.getAttribute('href');
        if (!href) {
          // 後援：全域第一個 YouTube 連結
          const globalA = tpl.content.querySelector('a[href*="youtube.com"], a[href*="youtu.be"]');
          if (globalA) href = globalA.getAttribute('href');
        }
      }
      if (!href) {
        // 允許直接從 img data-href 取用
        const dataHref = n.getAttribute && n.getAttribute('data-href');
        if (dataHref) href = dataHref;
      }
      if (href) {
        const cls = (anchor && anchor.getAttribute && anchor.getAttribute('class')) || (inYoutube ? 'youtube' : '');
        const classAttr = cls ? ` class="${cls}"` : '';
        htmlFrag = `<a${classAttr} href="${href}" target="_blank" rel="noopener noreferrer">${htmlFrag}</a>`;
      }
      // 字音字形（glyphSet）分頁斷點判定（對應 NBK-1512）：
      // 依原始 HTML 結構分辨每個 wordRadical 圖片屬於「讀音（多音字的某個音）」或「形近字」。
      // 判斷關鍵在「該段有沒有釋義」，而非只看 wordRadicalContent 是否為空：
      // - 讀音：一定有釋義。多義時用後接的 numberParagraph（wordRadicalContent 為空）；
      //   單義時（如 L2「勾」ㄍㄡˋ）改把釋義直接內嵌在 wordRadicalContent，內含
      //   .wordSense(例/解) / .wordSenseContent / 巢狀 .paragraph 等釋義結構。
      // - 形近字：沒有釋義，wordRadicalContent 只有例詞（純 ruby + 標點），無上述釋義結構。
      // 斷點規則：
      // - 第一個讀音不斷頁（與頁面既有內容同頁），其餘讀音各自獨立一頁（以首字注音為斷點）
      // - 形近字段落的第一個條目獨立一頁，後續形近字接續同頁
      let glyphBreak = false;
      if (glyphMode) {
        const wr = n.closest && n.closest('.wordRadical');
        if (wr) {
          const para = n.closest('.paragraph');
          const contentSpan = para && para.querySelector ? para.querySelector('.wordRadicalContent') : null;
          const radicalContent = contentSpan ? (contentSpan.textContent || '').trim() : '';
          // 釋義結構：wordRadicalContent 內若含 .wordSense / .wordSenseContent / 巢狀 .paragraph，
          // 代表這是「單義項直接內嵌釋義」的讀音，而非形近字。
          const hasDefinition = contentSpan && contentSpan.querySelector
            ? !!contentSpan.querySelector('.wordSense, .wordSenseContent, .paragraph')
            : false;
          // 形近字：wordRadicalContent 非空、且沒有任何釋義結構（只有例詞）
          const isSimilar = radicalContent.length > 0 && !hasDefinition;
          if (isSimilar) {
            // 形近字：僅在「由讀音（或段落開頭）進入形近字」的第一個條目才斷頁
            glyphBreak = prevGlyphRole !== 'similar';
            prevGlyphRole = 'similar';
          } else {
            // 讀音（含單義項內嵌釋義）：第一個讀音不斷頁，其餘讀音各自斷頁
            glyphBreak = glyphImgIndex > 0;
            prevGlyphRole = 'reading';
          }
          glyphImgIndex += 1;
        }
      }
      tokens.push({ kind: 'image', sub: null, glyphBreak, items: [{ html: htmlFrag }] });
    } else if (tag === 'br') {
      tokens.push({ kind: 'break', sub: null, items: [] });
    } else {
      const isPN = tag === 'span' && n.classList && (n.classList.contains('private_name') || n.classList.contains('flow_wave'));
      if (isPN) {
        tokens.push({ kind: 'normal', sub: null, items: splitPNItems(n) });
      } else {
        tokens.push({ kind: 'normal', sub: null, items: [{ html: n.outerHTML }] });
      }
    }
    n = walker.nextNode();
  }

  const cols = [];
  let bufNorm = [];
  let currentSub = null; // 目前段落的子型別（jie / li），用於普通內容著色
  // 是否讓後續溢出欄位的第一格留白：'num' 來源於數字 sign；'tag' 來源於例/解
  let indentNextCols = false;
  // 縮排用的空白格（不能用 null，因為後處理會 filter 掉 null）
  const INDENT_GAP = { html: '' };
  const meaningMode = !!opts.meaningMode;
  let meaningFirstColFlushed = false;
  // 僅數字的 <sign>（例：<sign>1</sign>）
  const isNumericSign = (it) => {
    if (!it || !it.html || typeof it.html !== 'string') return false;
    if (!/^<sign[\s>]/.test(it.html)) return false;
    const text = it.html.replace(/<[^>]+>/g, '').trim();
    return /^[0-9]+\.?$/.test(text);
  };
  // 僅含「.」的 <sign>（如 <sign>.</sign>），用於略過數字標題後的小數點
  const isDotOnlySign = (it) => {
    if (!it || !it.html || typeof it.html !== 'string') return false;
    if (!/^<sign[\s>]/.test(it.html)) return false;
    const text = it.html.replace(/<[^>]+>/g, '').trim();
    return text === '.';
  };
  // 為數字 sign 加上圈號外觀的 class
  const withNumberCircleClass = (it) => {
    if (!it || typeof it.html !== 'string') return it;
    let html = it.html;
    // 移除 sign 內數字後面的小數點（如 <sign>1.</sign> → <sign>1</sign>）
    html = html.replace(/(\d+)\.\s*(<\/sign>)/, '$1$2$3');
    // 若已有 class，附加 numSign，否則新增
    if (/^<sign\b[^>]*class=/.test(html)) {
      html = html.replace(/^<sign\b([^>]*class=["'])([^"']*)(["'])/,
        (_m, p1, p2, p3) => `<sign${p1}${p2} numSign${p3}`);
    } else {
      html = html.replace(/^<sign\b/, '<sign class="numSign"');
    }
    return { ...it, html };
  };
  // 依欄高切割為多欄
  const pushColsBy = (rows, items, type, sub, extra = {}) => {
    for (let i = 0; i < items.length; i += rows) {
      cols.push({ type, sub: sub || null, rows, items: items.slice(i, i + rows), ...extra });
    }
    if (items.length === 0) cols.push({ type, sub: sub || null, rows, items: [], ...extra });
  };
  // 生字大框（.word）字數過多時會被切成多個 'word' 欄。這些欄同屬「一個標題」，
  // 不該各自成為 chunkPagesByCols 的分頁斷點，否則長標題（如 7 字成語）會被拆到不同頁。
  // 只保留切割後第一欄為斷點，其餘標記 wordCont，讓分頁視為同一詞條的延續。
  const pushWordCols = (items) => {
    const start = cols.length;
    pushColsBy(ROWS_WORD, items, 'word', null);
    for (let k = start + 1; k < cols.length; k++) cols[k].wordCont = true;
  };
  const flushNorm = () => {
    if (bufNorm.length) {
      while (bufNorm.length > 0) {
        const isMeaning = indentNextCols === 'meaning';
        const needIndent = indentNextCols && !isNumericSign(bufNorm[0]) && !(isMeaning && !meaningFirstColFlushed);
        const gaps = typeof indentNextCols === 'number' ? indentNextCols : 1;
        const chunkSize = needIndent ? ROWS_NORMAL - gaps : ROWS_NORMAL;
        const sl = bufNorm.splice(0, chunkSize);
        if (needIndent) {
          for (let g = 0; g < gaps; g++) sl.unshift(INDENT_GAP);
        }
        cols.push({ type: 'normal', sub: currentSub, rows: ROWS_NORMAL, items: sl });
        if (indentNextCols === 'tag') indentNextCols = false;
        if (isMeaning) meaningFirstColFlushed = true;
      }
      bufNorm = [];
    }
  };

  // 若內容包含圖片且為字音字形模式：採用「圖片起頭分欄」規則
  const hasImage = glyphMode && tokens.some(t => t.kind === 'image');

  if (hasImage) {
    let openCol = null; // 目前開啟中的欄

    const closeOpenCol = () => {
      if (openCol) {
        // 僅在本欄有實際內容時才輸出（避免只有縮排空白或完全沒有字時仍產生空欄）
        const hasRealItem = (openCol.items || []).some(it => !!it);
        if (!hasRealItem) { openCol = null; return; }
        openCol.rows = ROWS_PER_COL;
        cols.push(openCol);
        openCol = null;
      }
    };

    // 將下一欄起始補上縮排用的空格：
    // - 'tag'：只補 1 格，且僅補一次
    // - 數值 1/2...：每次換到新欄都補對應的格數（圖片使用 2）
    const pushIndentGaps = (target) => {
      if (!indentNextCols) return;
      const gaps = typeof indentNextCols === 'number' ? indentNextCols : 1;
      for (let g = 0; g < gaps; g++) target.items.push(INDENT_GAP);
      if (indentNextCols === 'tag') indentNextCols = false;
    };

    let breakPending = false;
    for (const t of tokens) {
      if (t.kind === 'break') {
        breakPending = true;
        continue;
      }

      if (t.kind === 'image') {
        flushNorm();
        closeOpenCol();
        breakPending = false;  // 圖片重置 break 狀態
        openCol = { type: 'normal', sub: null, rows: ROWS_PER_COL, items: [], imgFollow: false, glyphBreak: t.glyphBreak === true };
        for (const it of t.items) {
          openCol.items.push(it);
        }
        indentNextCols = 2;
        continue;
      }

      if (t.kind === 'tag') {
        flushNorm();
        closeOpenCol();
        const subOverride = (t.sub === 'li' || t.sub === 'ci') ? 'jie' : t.sub;
        currentSub = (t.sub === 'ci' || t.follow === 'blue') ? 'blue' : t.sub;
        if (t.items.length > 1) {
          const merged = [...t.items];
          merged[0] = { ...merged[0], tagSub: subOverride };
          while (merged.length > 0) {
            const sl = merged.splice(0, ROWS_PER_COL);
            // barrier：同上，避免標點避頭把前一欄尾字倒進帶色的詞/例欄。
            cols.push({ type: 'normal', sub: currentSub, rows: ROWS_PER_COL, items: sl, barrier: true });
          }
        } else {
          cols.push({ type: 'tag', sub: subOverride, rows: ROWS_TAG, items: [...t.items], offset: false, follow: t.follow || null });
        }
        breakPending = false;
        indentNextCols = false;
        continue;
      }

      // 普通內容：延遲重置
      if (breakPending) {
        currentSub = null;
        breakPending = false;
      }

      // 其他內容：若有開啟中的圖片欄，則把內容依序塞入其下方
      if (openCol) {
        // 圖片起頭的欄位：僅允許「圖片 + 6 個字」，第 7 個字即換欄
        // 其餘一般欄位仍為 8 格
        let capacity = ROWS_PER_COL;
        const head = openCol.items[0];
        const headIsImg = head && typeof head.html === 'string' && /<img[\s>]/.test(head.html);
        if (glyphMode && headIsImg) capacity = ROWS_PER_COL - 1;
        for (const it of t.items) {
          if (isDotOnlySign(it)) continue;
          if (isNumericSign(it)) {
            // 僅當上一格為圖片時才斷到下一欄；否則接續當前欄
            const last = openCol.items[openCol.items.length - 1];
            const lastIsImg = last && typeof last.html === 'string' && /<img[\s>]/.test(last.html);
            if (lastIsImg) {
              cols.push({ ...openCol });
              openCol = { type: 'normal', sub: null, rows: ROWS_PER_COL, items: [withNumberCircleClass(it)], imgFollow: true };
              indentNextCols = 'num';
            } else {
              openCol.items.push(withNumberCircleClass(it));
            }
            continue;
          }
          openCol.items.push(it);
          // 若超過容量，先關閉本欄並另起一欄續寫（直到下一張圖片出現前都屬同一段）
          if (openCol.items.length > capacity) {
            // 取出超出的那一個字，帶到下一欄
            const overflowItem = openCol.items.pop();
            cols.push({ ...openCol });
            openCol.items = [];
            openCol.imgFollow = true; // 後續欄皆與圖片底部對齊
            openCol.glyphBreak = false; // 分頁斷點只保留在該段第一欄（含圖片），續接欄不再斷頁
            pushIndentGaps(openCol);
            if (overflowItem) openCol.items.push(overflowItem);
          }
        }
        continue;
      }

      // 沒有圖片欄時，沿用原本規則
      if (t.kind === 'word') {
        flushNorm();
        pushWordCols(t.items);
        currentSub = null;
        indentNextCols = false;
      } else if (t.kind === 'tag') {
        // '例'、'詞' 在 glyphSet 中套用與 '解' 相同樣式
        const subOverride = (t.sub === 'li' || t.sub === 'ci') ? 'jie' : t.sub;
        // 從第一格開始
        flushNorm(); cols.push({ type: 'tag', sub: subOverride, rows: ROWS_TAG, items: [...t.items], offset: false, follow: t.follow || null });
        currentSub = (t.sub === 'ci' || t.follow === 'blue') ? 'blue' : t.sub;
        // 後續內容不需留白
        indentNextCols = false;
      } else {
        for (const it of t.items) {
          if (isDotOnlySign(it)) continue;
          if (isNumericSign(it)) {
            // 尚未有圖片欄但遇到數字：另起新欄，直接從第一格開始（不留空）
            flushNorm();
            currentSub = null;
            openCol = { type: 'normal', sub: null, rows: ROWS_PER_COL, items: [withNumberCircleClass(it)] };
            indentNextCols = 'num';
            continue;
          }
          // 若已由數字 sign 開啟欄位，則後續內容接續此欄
          if (openCol) {
            const capacity = ROWS_PER_COL;
            openCol.items.push(it);
            if (openCol.items.length > capacity) {
              const overflowItem = openCol.items.pop();
              cols.push({ ...openCol });
              openCol.items = [];
              pushIndentGaps(openCol);
              if (overflowItem) openCol.items.push(overflowItem);
            }
            continue;
          }
          // 一般情況：照原本緩衝規則
          bufNorm.push(it);
          while (bufNorm.length >= ROWS_NORMAL) {
            const needIndent = indentNextCols && !isNumericSign(bufNorm[0]);
            const gaps = typeof indentNextCols === 'number' ? indentNextCols : 1;
            const chunkSize = needIndent ? ROWS_NORMAL - gaps : ROWS_NORMAL;
            const sl = bufNorm.splice(0, chunkSize);
            if (needIndent) {
              for (let g = 0; g < gaps; g++) sl.unshift(INDENT_GAP);
            }
            if (indentNextCols === 'tag') indentNextCols = false;
            cols.push({ type: 'normal', sub: currentSub, rows: ROWS_NORMAL, items: sl });
          }
        }
      }
    }

    closeOpenCol();
    flushNorm();
  } else {
    let breakPending = false;
    for (const t of tokens) {
      if (t.kind === 'break') {
        flushNorm();
        breakPending = true;
        meaningFirstColFlushed = false;
        if (indentNextCols === 'meaning') indentNextCols = false;
        continue;
      }
      if (t.kind === 'word') {
        flushNorm();
        pushWordCols(t.items);
        currentSub = null;
        meaningFirstColFlushed = false;
        indentNextCols = false;
        breakPending = false;
      } else if (t.kind === 'tag') {
        flushNorm();
        currentSub = (t.sub === 'ci' || t.follow === 'blue') ? 'blue' : t.sub;
        if (meaningMode && t.sub === 'li') {
          meaningFirstColFlushed = false;
          indentNextCols = 'meaning';
          t.items.forEach((it, i) => {
            if (i === 0) {
              bufNorm.push({ ...it, isInlineTag: true, tagSub: t.sub });
            } else {
              bufNorm.push(it);
            }
          });
        } else if (t.items.length > 1) {
          const merged = [...t.items];
          const subOverride = (t.sub === 'li' || t.sub === 'ci') ? 'jie' : t.sub;
          merged[0] = { ...merged[0], tagSub: subOverride };
          // 「詞/例（含 inline 例詞）」欄標記為 barrier：不得成為標點避頭重排的溢位落點，
          // 否則前一欄定義的尾字會被倒進來、跟著吃 sub-blue 而誤上色（NBK：蘋「用。」變藍）。
          pushColsBy(ROWS_NORMAL, merged, 'normal', currentSub, { barrier: true });
          meaningFirstColFlushed = false;
          if (indentNextCols === 'meaning') indentNextCols = false;
        } else {
          pushColsBy(ROWS_TAG, t.items, 'tag', t.sub);
          meaningFirstColFlushed = false;
          if (indentNextCols === 'meaning') indentNextCols = false;
        }
        if (indentNextCols !== 'meaning') indentNextCols = false;
        breakPending = false;
      } else {
        for (const it of t.items) {
          if (breakPending) {
            flushNorm();
            currentSub = null;
            indentNextCols = false;
            breakPending = false;
            meaningFirstColFlushed = false;
            if (indentNextCols === 'meaning') indentNextCols = false;
          }
          if (isNumericSign(it)) {
            flushNorm();
            currentSub = null;
            meaningFirstColFlushed = false;
            indentNextCols = 'num';
            bufNorm.push(withNumberCircleClass(it));
            continue;
          }
          bufNorm.push(it);
          while (bufNorm.length >= ROWS_NORMAL) {
            const isMeaning = indentNextCols === 'meaning';
            const needIndent = indentNextCols && !isNumericSign(bufNorm[0]) && !(isMeaning && !meaningFirstColFlushed);
            const chunkSize = needIndent ? ROWS_NORMAL - 1 : ROWS_NORMAL;
            const sl = bufNorm.splice(0, chunkSize);
            if (needIndent) sl.unshift(INDENT_GAP);
            cols.push({ type: 'normal', sub: currentSub, rows: ROWS_NORMAL, items: sl });
            if (indentNextCols === 'meaning') meaningFirstColFlushed = true;
          }
        }
      }
    }
    flushNorm();
  }

  // 工具：是否為「上括號」類的標點（需避尾）
  const isOpeningBracket = (it) => {
    if (!it || !it.html || typeof it.html !== 'string') return false;
    if (!/^<sign[\s>]/.test(it.html)) return false;
    const text = it.html.replace(/<[^>]+>/g, '').trim();
    const OPEN = '（[｛「『〈《【〔﹝︵︷︹︻︽︿';
    return text && OPEN.indexOf(text[0]) !== -1;
  };

  // 工具：是否為「不可被溢位污染」的欄位。
  // word/tag 本來就不接受標點避頭/避尾的遞移；barrier 欄（帶色的詞/例欄）比照辦理，
  // 遇到時改為在其前方另開空白 normal 欄承接，避免帶色欄被前一欄的字倒進來而誤上色。
  const isBarrierCol = (c) => !!c && (c.type === 'word' || c.type === 'tag' || c.barrier === true);

  // 規則：若某欄最後一個實際內容為「上括號」，則移至下一欄最上方
  for (let i = 0; i < cols.length; i++) {
    const curr = cols[i];
    const items = curr.items || [];
    const lastIdx = items.length - 1;
    if (lastIdx >= 0 && isOpeningBracket(items[lastIdx])) {
      let moved = items[lastIdx];
      items[lastIdx] = null;

      // 目標欄：下一欄，若不存在或為 word 欄，則在其前方插入普通欄
      let j = i + 1;
      while (true) {
        // 不允許把括號/遞移內容塞進 word/tag 欄首，避免「標題/標籤」被擠下去
        if (!cols[j] || isBarrierCol(cols[j])) {
          cols.splice(j, 0, { type: 'normal', sub: null, rows: ROWS_PER_COL, items: [] });
        }
        const target = cols[j];
        const tCap = target.rows || ROWS_PER_COL;
        // 插在「前導縮排空格」之後：避免上括號被排到編號懸掛縮排(INDENT_GAP)上方，
        // 導致空格夾在括號與其後字之間（如「通」欄末的『「』移入時，變成 [「,空,癲…]）。
        let insertAt = 0;
        while (insertAt < target.items.length
          && target.items[insertAt] && target.items[insertAt].html === '') insertAt++;
        target.items.splice(insertAt, 0, moved);
        if (target.items.length <= tCap) break;
        // 目標欄溢出 → 將最後一個遞移到下一個可用（非 word）的欄位
        moved = target.items.pop();
        j += 1;
      }
    }
  }
  // 避尾後先清除 null，讓避頭檢查能看到真正的首項
  for (const col of cols) {
    if (col.type !== 'normal') continue;
    col.items = col.items.filter(it => it !== null && it !== undefined);
  }
  // 標點避頭（上限 9 格）：
  // - 若前一欄未滿 9 格，將本欄起首標點移到前一欄末尾
  // - 若前一欄已滿 9 格，改在本欄最上方補一格空白，讓標點落在第 2 格，
  //   如因此溢出則把最後一個字遞移到後續欄位，確保每欄不超過 9 格
  // 標點避頭：僅對非數字的 <sign> 生效
  const isPunct = (it) => it && typeof it.html === 'string' && /^<sign[\s>]/.test(it.html) && !isNumericSign(it);
  for (let i = 1; i < cols.length; i++) {
    while (cols[i].items.length > 0 && isPunct(cols[i].items[0]) && !isOpeningBracket(cols[i].items[0])) {
      const prev = cols[i - 1];
      const curr = cols[i];
      const prevCap = (prev && prev.rows) ? prev.rows : ROWS_PER_COL;
      const currCap = curr.rows || ROWS_PER_COL;

      // 只允許把標點搬到「一般欄」末尾；避免污染 tag 欄（會讓第 1 格上色套錯字）
      if (prev.type === 'normal' && !prev.barrier && (prev.items || []).length < prevCap) {
        // 前一欄還有容量，直接把標點移到前一欄末尾（僅 normal）
        const punct = curr.items.shift();
        prev.items.push(punct);
        // rows 以容量為上限
        prev.rows = Math.min(prevCap, Math.max(prev.rows || 0, prev.items.length));
        continue;
      }

      // 兩種情況：
      // A) 前一欄為 word/tag → 不動前一欄，改於本欄前補一格空白，讓標點落在第 2 格
      // B) 前一欄非 word 且已滿 → 讓前欄最後一格留白，並把該字搬到本欄第一格
      const punct = curr.items[0];
      if (prev.type === 'word' || prev.type === 'tag' || prev.barrier) {
        const rest = curr.items.slice(1);
        curr.items = [null, punct, ...rest];

        // 若本欄超出容量，將最後一個字往後遞移（遇到 word 時插入 normal 欄）
        let overflow = null;
        while (curr.items.length > currCap) {
          overflow = curr.items.pop();
          let j = i + 1;
          while (overflow && j < cols.length) {
            if (!cols[j] || isBarrierCol(cols[j])) {
              cols.splice(j, 0, { type: 'normal', sub: null, rows: ROWS_PER_COL, items: [] });
            }
            cols[j].items.unshift(overflow);
            const jCap = cols[j].rows || ROWS_PER_COL;
            if (cols[j].items.length > jCap) {
              overflow = cols[j].items.pop();
              j += 1;
            } else {
              overflow = null;
            }
          }
          if (overflow) {
            cols.push({ type: 'normal', sub: null, rows: ROWS_PER_COL, items: [overflow] });
            overflow = null;
          }
        }
        break;
      } else {
        const movedFromPrev = prev.items[prevCap - 1] || null;
        // 將前一欄最後一格留白
        prev.items[prevCap - 1] = null;
        // 一般情況：構造 [前欄末字, 標點, 其餘]
        const rest = curr.items.slice(1);
        curr.items = [movedFromPrev, punct, ...rest];

        // 若本欄超出容量，將最後一個字往後遞移
        let overflow = null;
        while (curr.items.length > currCap) {
          overflow = curr.items.pop();
          let j = i + 1;
          while (overflow && j < cols.length) {
            // 若目標為 word，則在其前插入 normal 欄承接溢出
            if (!cols[j] || isBarrierCol(cols[j])) {
              cols.splice(j, 0, { type: 'normal', sub: null, rows: ROWS_PER_COL, items: [] });
            }
            cols[j].items.unshift(overflow);
            const jCap = cols[j].rows || ROWS_PER_COL;
            if (cols[j].items.length > jCap) {
              overflow = cols[j].items.pop();
              j += 1;
            } else {
              overflow = null;
            }
          }
          if (overflow) {
            cols.push({ type: 'normal', sub: null, rows: ROWS_PER_COL, items: [overflow] });
            overflow = null;
          }
        }
        // 完成本欄處理後跳出 while，避免無限迴圈
        break;
      }
    }
  }
  // 清理避尾/避頭在欄內留下的 null 空格
  for (const col of cols) {
    if (col.type !== 'normal') continue;
    col.items = col.items.filter(it => it !== null && it !== undefined);
  }
  return { columns: cols, wordItems: titleRubies.map(x => ({ html: x })) };
}

export function chunkPagesByCols(cols, colsPerPage = DEFAULT_COLS_PER_PAGE) {
  /**
   * 分頁規則（依使用者需求）：
   * - 以 `type === "word"` 作為新詞條起點：新標題一定從新頁開始。
   * - 字音字形（glyphSet）：以 `glyphBreak === true` 的欄作為分頁斷點（對應 NBK-1512）——
   *   多音字不同讀音各自獨立一頁、形近字段落獨立一頁。此斷點由 htmlToColumns 依原始 HTML
   *   結構（wordRadical / wordRadicalContent）標記。
   * - 同一群組（詞條/讀音/形近字）內容可跨多頁延續，直到遇到下一個斷點。
   * - `tag`（解/例）不能被丟在頁尾：若本頁剩餘欄位不足以放下「tag + 至少一欄後續內容」，
   *   則將 tag（與其後續內容）移到下一頁，避免出現「第 2 頁看起來排版怪、例句跑位」。
   */

  if (!cols || !cols.length) return [[]];

  const isWord = c => !!c && c.type === 'word';
  const isTag = c => !!c && c.type === 'tag';
  // 分頁斷點：詞條標題（word）或字音字形的讀音/形近字斷點（glyphBreak）。
  // 注意：長標題被切成多欄時，只有第一欄是斷點；後續碎片標了 wordCont，屬同一詞條的延續，不得斷頁。
  const isBreak = c => !!c && ((c.type === 'word' && !c.wordCont) || c.glyphBreak === true);

  // 先切成以斷點（word 或 glyphBreak）為起點的群組
  const groups = [];
  let gStart = 0;
  for (let idx = 0; idx < cols.length; idx++) {
    if (idx !== gStart && isBreak(cols[idx])) {
      groups.push(cols.slice(gStart, idx));
      gStart = idx;
    }
  }
  groups.push(cols.slice(gStart));

  const pages = [];

  const pushPage = (pageCols) => {
    if (!pageCols || !pageCols.length) return;
    pages.push(pageCols);
  };

  const paginateOneGroup = (groupCols) => {
    const out = [];
    let i = 0;
    while (i < groupCols.length) {
      const page = [];
      while (i < groupCols.length && page.length < colsPerPage) {
        const col = groupCols[i];
        // 安全保護：若群組內又遇到斷點（word 或 glyphBreak，理論上不該發生），強制換頁
        if (isBreak(col) && page.length) break;

        const remaining = colsPerPage - page.length;
        const next = groupCols[i + 1];

        // 規則：tag（解/例）不能落在頁尾，且必須與「至少 1 欄後續內容」同頁
        // 因此至少要有 2 欄空間（tag + 下一欄內容）才允許放入本頁。
        if (isTag(col) && remaining < 2 && next) {
          break;
        }

        page.push(col);
        i += 1;
      }

      // 收尾：避免頁尾是 word/tag 且後面還有內容（會讓下一頁開頭很怪）
      while (page.length > 1) {
        const last = page[page.length - 1];
        if ((isWord(last) || isTag(last)) && i < groupCols.length) {
          page.pop();
          i -= 1;
        } else {
          break;
        }
      }

      // 保底：避免空頁死循環
      if (!page.length && i < groupCols.length) {
        page.push(groupCols[i]);
        i += 1;
      }

      out.push(page);
    }
    return out;
  };

  // 依群組裝入頁面；每個群組（詞條）都從新頁開始
  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi] || [];
    if (!group.length) continue;
    const groupPages = paginateOneGroup(group);
    groupPages.forEach(p => pushPage(p));
  }

  return pages.length ? pages : [[]];
}

export function countCols(html) {
  const { columns } = htmlToColumns(html || '');
  return (columns || []).length;
}

export const WORDING_MAP = {
  meaningSet: '字義教學',
  wordsSet: '語詞教學',
  idiomSet: '生字成語',
  glyphSet: '字音字形'
};

export const EXTENSION_TYPE = {
  wordsSet: {
    fristName: '語詞',
    otherName: '延伸'
  },
  idiomSet: {
    fristName: '成語',
    otherName: '補充'
  }
}

// 將 extensionList 轉為可點選的條目（語詞/延伸 → 解釋/例句）
export function buildEntriesFromExtension(extensionList = [], extensionTypeKey = 'wordsSet', extensionData = []) {
  const entries = [];
  extensionData.forEach((item, index) => {
    const ext = extensionList.find(e => e.index === index);
    if (!ext || !ext.htmlJson) return;
    const htmlJson = ext.htmlJson || {};
    const html = htmlJson.html || htmlJson.content || htmlJson.body || '';
    const seg = extractSegments(html, { includeWord: extensionTypeKey === 'wordsSet' || extensionTypeKey === 'idiomSet' });
    const head = index === 0 ? (EXTENSION_TYPE[extensionTypeKey] && EXTENSION_TYPE[extensionTypeKey].fristName || '語詞') : (EXTENSION_TYPE[extensionTypeKey] && EXTENSION_TYPE[extensionTypeKey].otherName || '延伸');
    const num = index === 0 ? '' : String(index);

    if (seg.hasExplain) entries.push({
      label: `${head}解釋${num}`,
      html: seg.explainHTML,
      audio: item.CommentAudio || '',
      extIndex: index,
      kind: 'explain'
    });
    if (seg.hasExample) entries.push({
      label: `${head}例句${num}`,
      html: seg.exampleHTML,
      audio: item.ExampleAudio || '',
      extIndex: index,
      kind: 'example'
    });
  });
  return entries;
}

export const convertIdiomResource = data =>
  Object.entries(data).map(([key, item]) => ({ label: key, value: item.RelatedWords[0], relatedWords: item.RelatedWords }))

export const convertWordGridResource = data => data.map(item => ({ label: item.Name, value: item.Name, relatedWords: [item.Name] }))
